// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {acquireIdleVoiceInputContext, isVoiceInputSourceLive} from '@app/features/voice/engine/VoiceInputAudioContext';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import NoiseSuppressionAvailability, {
	type NoiseSuppressionFailureReason,
} from '@app/features/voice/utils/noise_suppression/NoiseSuppressionAvailability';
import {getNoiseSuppressionBackendDescriptor} from '@app/features/voice/utils/noise_suppression/NoiseSuppressionBackends';
import {
	readRequestedNoiseSuppressionBackend,
	resolveRuntimeVoiceProcessing,
} from '@app/features/voice/utils/noise_suppression/NoiseSuppressionRuntime';
import {
	getActiveInputDeviceLabel,
	getActiveVoiceProcessingMode,
	resolveVoiceProcessingFromStateForDeviceLabel,
} from '@app/features/voice/utils/VoiceProcessingProfile';

const logger = new Logger('DeepFilter');

const DEEP_FILTER_WASM_URL = new URL('./deepfilternet3/df_bg.wasm', import.meta.url).href;
const DEEP_FILTER_MODEL_URL = new URL('./deepfilternet3/DeepFilterNet3_onnx.tar.gz', import.meta.url).href;
const DEEP_FILTER_PROCESSOR_NAME = 'fluxer-deep-filter';
const DEEP_FILTER_ASSET_IDLE_MS = 20_000;
const DEEP_FILTER_ASSET_TOTAL_MS = 10 * 60_000;
const DEEP_FILTER_ASSET_TASK_BUDGET_MS = 8;
const DEEP_FILTER_BUILD_TIMEOUT_MS = 8000;
const DEEP_FILTER_ATTEN_LIM_DB = 30;
const DEEP_FILTER_INPUT_GAIN = 10;
const DEEP_FILTER_OUTPUT_GAIN = 0.1;
const DEEP_FILTER_HIGH_PASS_HZ = 60;
const DEEP_FILTER_PRIMED_TIMEOUT_MS = 500;
const DEEP_FILTER_RESET_TIMEOUT_MS = 2000;
const DEEP_FILTER_IDLE_DISPOSE_MS = 60_000;
const DEEP_FILTER_MODULE_RELEASE_MS = 5 * 60_000;
const DEEP_FILTER_JOIN_WAIT_MS = 2000;
const LEVEL_FAULT_MIN_INPUT_RMS = 10 ** (-50 / 20);
const LEVEL_FAULT_REPORTS = 4;
const LIVENESS_CHECK_INTERVAL_MS = 250;
const LIVENESS_MAX_CHECK_DELAY_MS = 2000;
const LIVENESS_MAX_REPORT_GAP_SECONDS = 2;
const GZIP_MAGIC = [0x1f, 0x8b];
const TAR_MAGIC = [0x75, 0x73, 0x74, 0x61, 0x72];
const TAR_MAGIC_OFFSET = 257;

type DeepFilterHealthListener = (processingMs: number, audioMs: number, contextTime: number) => void;
type DeepFilterMuteState = () => {muted: boolean; changedAt: number};

interface DeepFilterNodeHandle {
	node: AudioWorkletNode;
	flushAndAwaitPrimed: (signal: AbortSignal) => Promise<void>;
	reset: () => Promise<boolean>;
	watch: (
		onFault: (error: Error) => void,
		pipelineV2Enabled: () => boolean,
		onHealth?: DeepFilterHealthListener,
		muteState?: DeepFilterMuteState,
	) => () => void;
	dispose: () => void;
}

export interface DeepFilterNodeLease {
	node: AudioWorkletNode;
	builtOverLiveSource: boolean;
	primed: (signal: AbortSignal) => Promise<void>;
	release: () => void;
}

type DeepFilterWorkletMessage =
	| {type: 'ready'; frameLength: number}
	| {type: 'primed'}
	| {type: 'reset'; ok: boolean}
	| {type: 'error'; message: string}
	| {
			type: 'health';
			inputRms: number;
			outputRms: number;
			nonFinite: boolean;
			frame: number;
			contextTime: number;
			processingMs: number;
			audioMs: number;
			idle?: boolean;
	  };

class DeepFilterUnavailableError extends Error {
	constructor(
		readonly reason: NoiseSuppressionFailureReason,
		message: string,
	) {
		super(message);
		this.name = 'DeepFilterUnavailableError';
	}
}

function isDeepFilterWorkletMessage(value: unknown): value is DeepFilterWorkletMessage {
	if (!value || typeof value !== 'object') return false;
	const type = (value as {type?: unknown}).type;
	return type === 'ready' || type === 'primed' || type === 'reset' || type === 'error' || type === 'health';
}

function markDeepFilterFailed(reason: NoiseSuppressionFailureReason, error: unknown): void {
	logger.warn('DeepFilterNet is unavailable; using browser noise suppression', {reason, error});
	NoiseSuppressionAvailability.markBackendFailed('deep_filter', reason, Date.now());
}

function toDeepFilterBuildFailure(error: unknown): DeepFilterUnavailableError {
	if (error instanceof DeepFilterUnavailableError) return error;
	return new DeepFilterUnavailableError('build', error instanceof Error ? error.message : String(error));
}

function raceAbort<T>(pending: Promise<T>, signal: AbortSignal): Promise<T> {
	if (signal.aborted) return Promise.reject(signal.reason);
	return new Promise((resolve, reject) => {
		const onAbort = () => reject(signal.reason);
		signal.addEventListener('abort', onAbort, {once: true});
		pending.then(
			(value) => {
				signal.removeEventListener('abort', onAbort);
				resolve(value);
			},
			(error: unknown) => {
				signal.removeEventListener('abort', onAbort);
				reject(error);
			},
		);
	});
}

interface FetchedDeepFilterAsset {
	readonly response: Response;
	readonly watch: (body: ReadableStream<Uint8Array>) => ReadableStream<Uint8Array>;
}

async function fetchAsset(url: string, controller: AbortController): Promise<FetchedDeepFilterAsset> {
	let idleTimer: ReturnType<typeof setTimeout> | undefined;
	let lastProgressAt = performance.now();
	const stopIdleTimer = () => {
		clearTimeout(idleTimer);
		controller.signal.removeEventListener('abort', stopIdleTimer);
	};
	const checkIdle = () => {
		const remaining = DEEP_FILTER_ASSET_IDLE_MS - (performance.now() - lastProgressAt);
		if (remaining > 0) {
			idleTimer = setTimeout(checkIdle, remaining);
			return;
		}
		controller.abort(
			new DeepFilterUnavailableError('assets', `No data from ${url} for ${DEEP_FILTER_ASSET_IDLE_MS} ms`),
		);
	};
	controller.signal.addEventListener('abort', stopIdleTimer, {once: true});
	idleTimer = setTimeout(checkIdle, DEEP_FILTER_ASSET_IDLE_MS);
	try {
		const response = await fetch(url, {credentials: 'same-origin', signal: controller.signal});
		if (!response.ok || !response.body) {
			throw new DeepFilterUnavailableError('assets', `${url} returned status ${response.status}`);
		}
		lastProgressAt = performance.now();
		let lastYieldAt = lastProgressAt;
		const watch = (body: ReadableStream<Uint8Array>) =>
			body.pipeThrough(
				new TransformStream<Uint8Array, Uint8Array>({
					transform(chunk, stream) {
						lastProgressAt = performance.now();
						stream.enqueue(chunk);
						if (lastProgressAt - lastYieldAt < DEEP_FILTER_ASSET_TASK_BUDGET_MS) return;
						// Cached assets can otherwise drain hundreds of stream microtasks in one browser task.
						const scheduler = (globalThis as typeof globalThis & {scheduler?: {yield?: () => Promise<void>}}).scheduler;
						const yielded = scheduler?.yield?.() ?? new Promise<void>((resolve) => setTimeout(resolve, 0));
						return yielded.then(() => {
							controller.signal.throwIfAborted();
							lastYieldAt = performance.now();
						});
					},
					flush: stopIdleTimer,
				}),
			);
		return {response, watch};
	} catch (error) {
		stopIdleTimer();
		throw error;
	}
}

async function compileDeepFilterWasm(controller: AbortController): Promise<WebAssembly.Module> {
	const {response, watch} = await fetchAsset(DEEP_FILTER_WASM_URL, controller);
	if (typeof WebAssembly.compileStreaming !== 'function') {
		return WebAssembly.compile(await new Response(watch(response.body!)).arrayBuffer());
	}
	const mediaType = response.headers.get('content-type')?.split(';')[0].trim().toLowerCase();
	if (mediaType === 'application/wasm') {
		// Compiling the fetched response itself lets the browser reuse the code it cached for this URL;
		// the download deadlines watch a copy of the body instead.
		void watch(response.clone().body!)
			.pipeTo(new WritableStream())
			.catch(() => {});
		return WebAssembly.compileStreaming(response);
	}
	return WebAssembly.compileStreaming(
		new Response(watch(response.body!), {headers: {'content-type': 'application/wasm'}}),
	);
}

function hasBytesAt(bytes: Uint8Array, offset: number, expected: ReadonlyArray<number>): boolean {
	return expected.every((value, index) => bytes[offset + index] === value);
}

async function readDeepFilterModel(body: ReadableStream<Uint8Array>): Promise<ArrayBuffer> {
	const buffer = await new Response(body).arrayBuffer();
	const bytes = new Uint8Array(buffer);
	if (hasBytesAt(bytes, 0, GZIP_MAGIC)) return buffer;
	if (hasBytesAt(bytes, TAR_MAGIC_OFFSET, TAR_MAGIC)) {
		throw new DeepFilterUnavailableError('model_encoding', 'DeepFilterNet model arrived decompressed');
	}
	throw new DeepFilterUnavailableError('assets', 'DeepFilterNet model is not a gzip archive');
}

async function withAssetDeadline<T>(download: (controller: AbortController) => Promise<T>): Promise<T> {
	const controller = new AbortController();
	const totalTimer = setTimeout(() => {
		controller.abort(new DeepFilterUnavailableError('assets', 'DeepFilterNet assets took too long to download'));
	}, DEEP_FILTER_ASSET_TOTAL_MS);
	try {
		return await download(controller);
	} catch (error) {
		controller.abort(error);
		const cause: unknown = controller.signal.reason;
		if (cause instanceof DeepFilterUnavailableError) throw cause;
		throw new DeepFilterUnavailableError('assets', cause instanceof Error ? cause.message : String(cause));
	} finally {
		clearTimeout(totalTimer);
	}
}

function fetchDeepFilterModel(controller: AbortController): Promise<ArrayBuffer> {
	return fetchAsset(DEEP_FILTER_MODEL_URL, controller).then(({response, watch}) =>
		readDeepFilterModel(watch(response.body!)),
	);
}

let assetsLoad: Promise<WebAssembly.Module> | null = null;
let prefetchedModel: ArrayBuffer | null = null;
let liveDeepFilterNodes = 0;
let assetsReleaseTimer: ReturnType<typeof setTimeout> | undefined;
let voiceCallActive = false;

export function setVoiceCallActive(active: boolean, newCall = false): void {
	if (active === voiceCallActive) return;
	voiceCallActive = active;
	if (active) {
		// A reconnect keeps the CPU budget fallback of the call it recovers; a new call may try DeepFilter again.
		NoiseSuppressionAvailability.releaseExpiredFailures(Date.now(), false, newCall);
		return;
	}
	// The release counts from the end of the call, not from a timer that kept waiting through it.
	if (liveDeepFilterNodes === 0 && NoiseSuppressionAvailability.deepFilterAssets === 'ready') {
		scheduleDeepFilterAssetRelease();
	}
}

function scheduleDeepFilterAssetRelease(): void {
	clearTimeout(assetsReleaseTimer);
	assetsReleaseTimer = setTimeout(() => {
		// A call can start its microphone, or a live graph switch back to DeepFilter, without a new session.
		if (voiceCallActive || isVoiceInputSourceLive()) {
			scheduleDeepFilterAssetRelease();
			return;
		}
		assetsLoad = null;
		prefetchedModel = null;
		NoiseSuppressionAvailability.setDeepFilterAssetState('idle');
	}, DEEP_FILTER_MODULE_RELEASE_MS);
}

function fetchDeepFilterAssets(): Promise<WebAssembly.Module> {
	return withAssetDeadline(async (controller) => {
		const [module, modelBytes] = await Promise.all([
			compileDeepFilterWasm(controller),
			fetchDeepFilterModel(controller),
		]);
		prefetchedModel = modelBytes;
		return module;
	});
}

async function takeDeepFilterModel(): Promise<ArrayBuffer> {
	const prefetched = prefetchedModel;
	prefetchedModel = null;
	if (prefetched) return prefetched;
	try {
		return await withAssetDeadline(fetchDeepFilterModel);
	} catch (error) {
		const failure = toDeepFilterBuildFailure(error);
		markDeepFilterFailed(failure.reason, failure);
		throw failure;
	}
}

export function loadDeepFilterAssets(signal?: AbortSignal): Promise<WebAssembly.Module> {
	if (!assetsLoad) {
		clearTimeout(assetsReleaseTimer);
		const load = fetchDeepFilterAssets();
		assetsLoad = load;
		NoiseSuppressionAvailability.setDeepFilterAssetState('loading');
		load.then(
			() => {
				if (assetsLoad !== load) return;
				NoiseSuppressionAvailability.setDeepFilterAssetState('ready');
				if (liveDeepFilterNodes === 0) scheduleDeepFilterAssetRelease();
			},
			(error: unknown) => {
				if (assetsLoad !== load) return;
				assetsLoad = null;
				markDeepFilterFailed(error instanceof DeepFilterUnavailableError ? error.reason : 'assets', error);
				NoiseSuppressionAvailability.setDeepFilterAssetState('idle');
			},
		);
	}
	return signal ? raceAbort(assetsLoad, signal) : assetsLoad;
}

function isDeepFilterRequested(): boolean {
	return (
		getActiveVoiceProcessingMode(VoiceSettings) !== 'studio' && readRequestedNoiseSuppressionBackend() === 'deep_filter'
	);
}

export function prefetchDeepFilterAssets(): void {
	if (!isDeepFilterRequested()) return;
	if (NoiseSuppressionAvailability.failures.get('deep_filter')?.active) return;
	if (NoiseSuppressionAvailability.deepFilterAssets !== 'idle') return;
	void loadDeepFilterAssets().catch(() => {});
}

const workletModules = new WeakMap<BaseAudioContext, Promise<void>>();

function addDeepFilterWorkletModule(context: BaseAudioContext): Promise<void> {
	let pending = workletModules.get(context);
	if (!pending) {
		pending = import('@app/features/voice/utils/noise_suppression/NoiseSuppressionWorkletAssets').then((assets) =>
			context.audioWorklet.addModule(assets.DEEP_FILTER_WORKLET_URL),
		);
		workletModules.set(context, pending);
		pending.catch(() => {
			if (workletModules.get(context) === pending) workletModules.delete(context);
		});
	}
	return pending;
}

type DeepFilterMessageListener = (message: DeepFilterWorkletMessage) => void;

function listenToDeepFilterNode(node: AudioWorkletNode): (listener: DeepFilterMessageListener) => () => void {
	const listeners = new Set<DeepFilterMessageListener>();
	node.port.onmessage = (event: MessageEvent) => {
		if (!isDeepFilterWorkletMessage(event.data)) return;
		for (const listener of [...listeners]) listener(event.data);
	};
	return (listener) => {
		listeners.add(listener);
		return () => {
			listeners.delete(listener);
		};
	};
}

function awaitDeepFilterReady(
	node: AudioWorkletNode,
	listen: (listener: DeepFilterMessageListener) => () => void,
	signal: AbortSignal,
): Promise<void> {
	return new Promise((resolve, reject) => {
		const finish = (error?: unknown) => {
			stopListening();
			node.onprocessorerror = null;
			signal.removeEventListener('abort', onAbort);
			if (error === undefined) resolve();
			else reject(error);
		};
		const onAbort = () => finish(signal.reason);
		signal.addEventListener('abort', onAbort, {once: true});
		const stopListening = listen((message) => {
			if (message.type === 'ready') finish();
			else if (message.type === 'error') {
				finish(new DeepFilterUnavailableError('build', `DeepFilterNet worklet failed: ${message.message}`));
			}
		});
		node.onprocessorerror = () =>
			finish(new DeepFilterUnavailableError('build', 'DeepFilterNet worklet raised an error'));
	});
}

const voiceInputLevelTimes = new WeakMap<BaseAudioContext, number>();

export function reportVoiceInputLevel(context: BaseAudioContext, contextTime: number): void {
	if (!Number.isFinite(contextTime)) return;
	voiceInputLevelTimes.set(context, contextTime);
}

function watchDeepFilterNode(
	context: BaseAudioContext,
	node: AudioWorkletNode,
	listen: (listener: DeepFilterMessageListener) => () => void,
	onFault: (error: Error) => void,
	pipelineV2Enabled: () => boolean,
	onHealth?: DeepFilterHealthListener,
	muteState?: DeepFilterMuteState,
): () => void {
	let lastReportContextTime = context.currentTime;
	let levelFaultReports = 0;
	let lastCheckAtMs = performance.now();
	let enabled = pipelineV2Enabled();
	let lastMuteChange = muteState?.().changedAt;
	const syncMute = (): boolean => {
		const state = muteState?.();
		if (state?.muted || state?.changedAt !== lastMuteChange) {
			lastReportContextTime = context.currentTime;
			levelFaultReports = 0;
		}
		lastMuteChange = state?.changedAt;
		return state?.muted ?? false;
	};
	node.port.postMessage({type: 'pipeline', enabled});
	const checkLiveness = () => {
		const now = performance.now();
		const checkWasDelayed = now - lastCheckAtMs > LIVENESS_MAX_CHECK_DELAY_MS;
		lastCheckAtMs = now;
		if (syncMute()) return;
		if (checkWasDelayed) return;
		if (context.state !== 'running') {
			lastReportContextTime = context.currentTime;
			levelFaultReports = 0;
			return;
		}
		const levelTime = voiceInputLevelTimes.get(context);
		if (
			context.currentTime - lastReportContextTime > LIVENESS_MAX_REPORT_GAP_SECONDS &&
			levelTime !== undefined &&
			levelTime - lastReportContextTime > LIVENESS_MAX_REPORT_GAP_SECONDS &&
			context.currentTime - levelTime < LIVENESS_MAX_REPORT_GAP_SECONDS
		) {
			onFault(new Error('DeepFilterNet stopped processing audio'));
		}
	};
	const livenessTimer = setInterval(checkLiveness, LIVENESS_CHECK_INTERVAL_MS);
	const stopListening = listen((message) => {
		if (message.type === 'error') {
			onFault(new Error(`DeepFilterNet worklet failed: ${message.message}`));
			return;
		}
		if (message.type !== 'health') return;
		if (syncMute() || message.contextTime < lastReportContextTime) return;
		const nextEnabled = pipelineV2Enabled();
		if (nextEnabled !== enabled) {
			enabled = nextEnabled;
			node.port.postMessage({type: 'pipeline', enabled});
		}
		lastReportContextTime = message.contextTime;
		NoiseSuppressionAvailability.setDeepFilterLevels(context, message.inputRms, message.outputRms, message.contextTime);
		if (onHealth) {
			if (enabled) onHealth(message.processingMs, message.audioMs, message.contextTime);
		} else
			NoiseSuppressionAvailability.reportProcessingHealth(
				'deep_filter',
				message.processingMs,
				message.audioMs,
				pipelineV2Enabled(),
				'filter',
				node,
			);
		if (message.idle === true) return;
		const silentOutput = message.outputRms === 0 || message.nonFinite;
		levelFaultReports = message.inputRms > LEVEL_FAULT_MIN_INPUT_RMS && silentOutput ? levelFaultReports + 1 : 0;
		if (levelFaultReports >= LEVEL_FAULT_REPORTS) onFault(new Error('DeepFilterNet output went silent'));
	});
	node.onprocessorerror = () => onFault(new Error('DeepFilterNet worklet raised an error'));
	return () => {
		clearInterval(livenessTimer);
		stopListening();
		node.onprocessorerror = null;
	};
}

function awaitDeepFilterPrimed(
	listen: (listener: DeepFilterMessageListener) => () => void,
	signal: AbortSignal,
): Promise<void> {
	return new Promise((resolve) => {
		const finish = () => {
			clearTimeout(timer);
			stopListening();
			signal.removeEventListener('abort', finish);
			resolve();
		};
		const timer = setTimeout(finish, DEEP_FILTER_PRIMED_TIMEOUT_MS);
		signal.addEventListener('abort', finish, {once: true});
		const stopListening = listen((message) => {
			if (message.type === 'primed') finish();
		});
	});
}

function awaitDeepFilterReset(listen: (listener: DeepFilterMessageListener) => () => void): Promise<boolean> {
	return new Promise((resolve) => {
		const finish = (ok: boolean) => {
			clearTimeout(timer);
			stopListening();
			resolve(ok);
		};
		const timer = setTimeout(() => finish(false), DEEP_FILTER_RESET_TIMEOUT_MS);
		const stopListening = listen((message) => {
			if (message.type === 'reset') finish(message.ok);
			else if (message.type === 'error') finish(false);
		});
	});
}

async function createDeepFilterNode(
	context: AudioContext,
	attenuationLimit = DEEP_FILTER_ATTEN_LIM_DB,
): Promise<DeepFilterNodeHandle> {
	const isClosed = () => context.state === 'closed';
	if (isClosed()) throw new DOMException('Voice input AudioContext closed', 'AbortError');
	const supportedSampleRates = getNoiseSuppressionBackendDescriptor('deep_filter').supportedSampleRates ?? [];
	if (!supportedSampleRates.includes(context.sampleRate)) {
		const error = new DeepFilterUnavailableError('sample_rate', `DeepFilterNet cannot run at ${context.sampleRate} Hz`);
		markDeepFilterFailed(error.reason, error);
		throw error;
	}
	const module = await loadDeepFilterAssets();
	const modelBytes = await takeDeepFilterModel();
	const controller = new AbortController();
	const onStateChange = () => {
		if (isClosed()) {
			controller.abort(new DOMException('Voice input AudioContext closed', 'AbortError'));
		}
	};
	context.addEventListener('statechange', onStateChange);
	onStateChange();
	let runningMs = 0;
	let previousTick = performance.now();
	const deadline = setInterval(() => {
		const now = performance.now();
		if (context.state === 'running') runningMs += now - previousTick;
		previousTick = now;
		if (runningMs >= DEEP_FILTER_BUILD_TIMEOUT_MS) {
			controller.abort(new DeepFilterUnavailableError('build', 'DeepFilterNet did not start in time'));
		}
	}, 100);
	let node: AudioWorkletNode | null = null;
	let listen: ((listener: DeepFilterMessageListener) => () => void) | null = null;
	try {
		await raceAbort(addDeepFilterWorkletModule(context), controller.signal);
		node = new AudioWorkletNode(context, DEEP_FILTER_PROCESSOR_NAME, {
			numberOfInputs: 2,
			numberOfOutputs: 1,
			outputChannelCount: [1],
			channelCount: 1,
			channelCountMode: 'explicit',
			channelInterpretation: 'speakers',
			processorOptions: {
				wasmModule: module,
				attenLimDb: attenuationLimit,
				inputGain: DEEP_FILTER_INPUT_GAIN,
				outputGain: DEEP_FILTER_OUTPUT_GAIN,
				highPassHz: DEEP_FILTER_HIGH_PASS_HZ,
			},
		});
		listen = listenToDeepFilterNode(node);
		// processorOptions would be cloned, and the worklet thread keeps that clone until its next garbage collection.
		node.port.postMessage({type: 'model', bytes: modelBytes}, [modelBytes]);
		await awaitDeepFilterReady(node, listen, controller.signal);
	} catch (error) {
		if (node) {
			node.port.postMessage({type: 'dispose'});
			node.port.close();
			node.disconnect();
		}
		if (isClosed()) throw new DOMException('Voice input AudioContext closed', 'AbortError');
		const failure = toDeepFilterBuildFailure(error);
		markDeepFilterFailed(failure.reason, failure);
		throw failure;
	} finally {
		clearInterval(deadline);
		context.removeEventListener('statechange', onStateChange);
	}
	NoiseSuppressionAvailability.clearBackendFailure('deep_filter');
	const readyNode = node;
	const readyListen = listen;
	return {
		node: readyNode,
		flushAndAwaitPrimed: (signal) => {
			const primed = awaitDeepFilterPrimed(readyListen, signal);
			readyNode.port.postMessage({type: 'flush'});
			return primed;
		},
		reset: () => {
			const reset = awaitDeepFilterReset(readyListen);
			readyNode.port.postMessage({type: 'reset'});
			return reset;
		},
		watch: (onFault, pipelineV2Enabled, onHealth, muteState) =>
			watchDeepFilterNode(context, readyNode, readyListen, onFault, pipelineV2Enabled, onHealth, muteState),
		dispose: () => {
			readyNode.disconnect();
			readyNode.port.postMessage({type: 'dispose'});
			readyNode.port.close();
		},
	};
}

interface DeepFilterPoolEntry {
	readonly attenuationLimit: number;
	readonly build: Promise<DeepFilterNodeHandle>;
	readonly stopWatchingContext: () => void;
	holder: symbol | null;
	idleTimer: ReturnType<typeof setTimeout> | undefined;
	faulted: boolean;
	disposed: boolean;
	builtOverLiveSource: boolean;
}

const deepFilterPools = new WeakMap<BaseAudioContext, Set<DeepFilterPoolEntry>>();

function getDeepFilterPool(context: BaseAudioContext): Set<DeepFilterPoolEntry> {
	const pool = deepFilterPools.get(context) ?? new Set<DeepFilterPoolEntry>();
	deepFilterPools.set(context, pool);
	return pool;
}

function disposePoolEntry(pool: Set<DeepFilterPoolEntry>, entry: DeepFilterPoolEntry): void {
	if (entry.disposed) return;
	entry.disposed = true;
	entry.faulted = true;
	entry.stopWatchingContext();
	clearTimeout(entry.idleTimer);
	pool.delete(entry);
	liveDeepFilterNodes--;
	if (liveDeepFilterNodes === 0) scheduleDeepFilterAssetRelease();
	void entry.build.then(
		(handle) => handle.dispose(),
		() => {},
	);
}

function createPoolEntry(
	context: AudioContext,
	pool: Set<DeepFilterPoolEntry>,
	attenuationLimit: number,
): DeepFilterPoolEntry {
	const onStateChange = () => {
		if (context.state === 'closed') disposePoolEntry(pool, entry);
	};
	const entry: DeepFilterPoolEntry = {
		build: createDeepFilterNode(context, attenuationLimit),
		attenuationLimit,
		stopWatchingContext: () => context.removeEventListener('statechange', onStateChange),
		holder: null,
		idleTimer: undefined,
		faulted: false,
		disposed: false,
		builtOverLiveSource: false,
	};
	pool.add(entry);
	liveDeepFilterNodes++;
	clearTimeout(assetsReleaseTimer);
	context.addEventListener('statechange', onStateChange);
	onStateChange();
	entry.build.then(
		() => {
			entry.builtOverLiveSource = isVoiceInputSourceLive();
		},
		() => {
			disposePoolEntry(pool, entry);
		},
	);
	return entry;
}

function releasePoolEntry(
	pool: Set<DeepFilterPoolEntry>,
	entry: DeepFilterPoolEntry,
	token: symbol,
	used = false,
): void {
	if (entry.holder !== token) return;
	entry.holder = null;
	if (entry.faulted) {
		disposePoolEntry(pool, entry);
		return;
	}
	if (used) {
		const resetting = Symbol('deep-filter-reset');
		entry.holder = resetting;
		void entry.build
			.then((handle) => handle.reset())
			.then((ok) => {
				if (!ok) entry.faulted = true;
				releasePoolEntry(pool, entry, resetting);
			});
		return;
	}
	entry.idleTimer = setTimeout(() => disposePoolEntry(pool, entry), DEEP_FILTER_IDLE_DISPOSE_MS);
}

export function hasIdleDeepFilterNode(context: BaseAudioContext, attenuationLimit: number): boolean {
	for (const entry of getDeepFilterPool(context)) {
		if (entry.holder === null && !entry.faulted && entry.attenuationLimit === attenuationLimit) return true;
	}
	return false;
}

export async function acquireDeepFilterNode(
	context: AudioContext,
	signal: AbortSignal,
	onFault: () => void,
	attenuationLimit = DEEP_FILTER_ATTEN_LIM_DB,
	pipelineV2Enabled: () => boolean = () => false,
	onHealth?: DeepFilterHealthListener,
	muteState?: DeepFilterMuteState,
): Promise<DeepFilterNodeLease> {
	attenuationLimit = Number.isFinite(attenuationLimit)
		? Math.max(0, Math.min(100, attenuationLimit))
		: DEEP_FILTER_ATTEN_LIM_DB;
	await loadDeepFilterAssets(signal);
	signal.throwIfAborted();
	const pool = getDeepFilterPool(context);
	const idle = [...pool].find(
		(candidate) => candidate.holder === null && !candidate.faulted && candidate.attenuationLimit === attenuationLimit,
	);
	const entry = idle ?? createPoolEntry(context, pool, attenuationLimit);
	const token = Symbol('deep-filter-lease');
	entry.holder = token;
	clearTimeout(entry.idleTimer);
	let handle: DeepFilterNodeHandle;
	try {
		handle = await raceAbort(entry.build, signal);
	} catch (error) {
		releasePoolEntry(pool, entry, token);
		throw error;
	}
	const builtOverLiveSource = entry.builtOverLiveSource;
	entry.builtOverLiveSource = false;
	const stopWatching = handle.watch(
		(error) => {
			if (entry.holder !== token || entry.faulted) return;
			entry.faulted = true;
			pool.delete(entry);
			markDeepFilterFailed('runtime', error);
			onFault();
		},
		pipelineV2Enabled,
		onHealth,
		muteState,
	);
	let used = false;
	return {
		node: handle.node,
		builtOverLiveSource,
		primed: (primedSignal) => {
			// Flushing JavaScript rings does not clear the model's previous microphone audio.
			used = true;
			return handle.flushAndAwaitPrimed(primedSignal);
		},
		release: () => {
			if (entry.holder !== token) return;
			stopWatching();
			releasePoolEntry(pool, entry, token, used);
		},
	};
}

let microphoneSessionWarmup: Promise<void> | null = null;

async function buildIdleDeepFilterNode(context: AudioContext, signal?: AbortSignal): Promise<void> {
	const joinWait = new AbortController();
	const joinDeadline = setTimeout(() => joinWait.abort(), DEEP_FILTER_JOIN_WAIT_MS);
	const onAbort = () => joinWait.abort(signal?.reason);
	signal?.addEventListener('abort', onAbort, {once: true});
	if (signal?.aborted) onAbort();
	try {
		await loadDeepFilterAssets(joinWait.signal);
		if (isVoiceInputSourceLive()) return;
		const profile = resolveRuntimeVoiceProcessing(
			resolveVoiceProcessingFromStateForDeviceLabel(VoiceSettings, getActiveInputDeviceLabel(VoiceSettings)),
		);
		const lease = await acquireDeepFilterNode(
			context,
			joinWait.signal,
			() => {},
			profile.deepFilterNoiseReductionLevel,
		);
		lease.release();
	} finally {
		clearTimeout(joinDeadline);
		signal?.removeEventListener('abort', onAbort);
	}
}

async function warmDeepFilterNode(signal?: AbortSignal): Promise<void> {
	const lease = acquireIdleVoiceInputContext();
	if (!lease) return;
	await buildIdleDeepFilterNode(lease.context, signal)
		.catch(() => {})
		.finally(() => lease.release());
}

export function beginMicrophoneSession(signal?: AbortSignal, resetCpuFailures = false): void {
	NoiseSuppressionAvailability.releaseExpiredFailures(Date.now(), resetCpuFailures);
	prefetchDeepFilterAssets();
	if (!isDeepFilterRequested() || NoiseSuppressionAvailability.failures.get('deep_filter')?.active) return;
	const warmup = warmDeepFilterNode(signal);
	microphoneSessionWarmup = warmup;
	void warmup.finally(() => {
		if (microphoneSessionWarmup === warmup) microphoneSessionWarmup = null;
	});
}

export function awaitMicrophoneSessionWarmup(): Promise<void> {
	prefetchDeepFilterAssets();
	return microphoneSessionWarmup ?? Promise.resolve();
}
